import { BoxArrowDown, X } from '@phosphor-icons/react'
import { Icon, IconButton } from '@shopflo/ui'
import React from 'react'
import { useLocale } from 'src/lib/hooks/useLocal'
import { SubscriptionPlanDraft } from 'src/lib/types/subscriptions'
import { formatPlanDiscount } from 'src/lib/utils/subscriptions/subscriptionHelpers'
import {
  DEFAULT_ICON,
  DEFAULT_OFFER_PILL,
  DEFAULT_RIBBON,
  PREVIEW_TEXT,
  PRICE_PLACEHOLDER,
  STRIKE_PLACEHOLDER,
  ribbonGradient
} from './previewTheme'
import { PlanTitle, planDeliveryCount } from './planPreviewCopy'

const RECOMMENDED_RIBBON = ribbonGradient(DEFAULT_RIBBON)
const SELECTED_FILL =
  'linear-gradient(0deg, color-mix(in srgb, var(--flo-primary-dark-color) 6%, transparent), color-mix(in srgb, var(--flo-primary-dark-color) 6%, transparent)), #FFFFFF'

const DISCOUNT_PILL_SHADOW =
  'shadow-[0_0_0_0.5px_rgba(9,9,11,0.12),0_15px_11px_-6px_rgba(9,9,11,0.06),0_2px_9px_-6px_rgba(9,9,11,0.48),0_1px_2px_rgba(10,13,18,0.06),inset_0_0_0_0.5px_rgba(9,9,11,0.12),inset_0_7px_2px_rgba(255,255,255,0.15)]'

interface ChoosePlanSheetProps {
  plans: SubscriptionPlanDraft[]
  selectedLocalId?: string
  onSelect: (localId: string) => void
  onClose: () => void
  colors?: {
    offer?: string
    highlight?: string
    icon?: string
  }
}

const ChoosePlanSheet: React.FC<ChoosePlanSheetProps> = ({ plans, selectedLocalId, onSelect, onClose, colors }) => {
  const { t } = useLocale()

  const ribbon = colors?.highlight ? ribbonGradient(colors.highlight) : RECOMMENDED_RIBBON
  const discountPill = colors?.offer || DEFAULT_OFFER_PILL

  return (
    <div className='absolute inset-0 flex flex-col justify-end'>
      <div className='absolute inset-0 bg-black/60' onClick={onClose} />

      <div className='relative flex flex-col items-center gap-[11px]'>
        <IconButton
          id='subscription-preview-close-plans'
          IconComponent={X}
          variant='ghost'
          size='lg'
          onClick={onClose}
          className='h-[34px] w-[34px] rounded-full border-1 border-white/25 bg-white/[0.12] p-0 text-white backdrop-blur-[4px]'
        />

        <div className='w-full rounded-t-[15px] bg-white'>
          <div className='flex items-center gap-[11px] px-[19px] py-4'>
            <Icon
              IconComponent={BoxArrowDown}
              size='xl'
              className='shrink-0'
              iconProps={{
                className: 'opacity-60',
                weight: 'fill',
                style: { color: colors?.icon || DEFAULT_ICON }
              }}
            />
            <div className='flex flex-col'>
              <span className={`${PREVIEW_TEXT.sheetTitle} text-black`}>
                {t('subscriptions.preview_choose_plan')}
              </span>
              <span className={`${PREVIEW_TEXT.sheetSubtitle} text-[#0C0C0C]/60`}>
                {t('subscriptions.preview_choose_plan_subtitle')}
              </span>
            </div>
          </div>

          <ul className='flex flex-col gap-[11px] px-[15px] pb-2'>
            {plans.map(plan => {
              const isSelected = plan.localId === selectedLocalId
              const deliveries = planDeliveryCount(plan)
              const discount = formatPlanDiscount(plan, t)

              return (
                <li key={plan.localId} className='flex flex-col'>
                  {plan.isRecommended && (
                    <div
                      className='-mb-[11px] flex h-[39px] items-start rounded-t-[11px] px-[11px] py-1'
                      style={{ background: ribbon }}
                    >
                      <span className={`${PREVIEW_TEXT.ribbonLabel} text-[#5E3F02]/90`}>
                        {t('subscriptions.preview_recommended')}
                      </span>
                    </div>
                  )}
                  <button
                    type='button'
                    onClick={() => onSelect(plan.localId)}
                    className={`relative flex items-center gap-[7px] rounded-[11px] px-[15px] py-[9px] text-left ${
                      isSelected
                        ? 'shadow-[0_1px_2px_rgba(10,13,18,0.05),inset_0_0_0_1.5px_var(--flo-primary-dark-color)]'
                        : 'bg-white shadow-[0_0_0_1px_color-mix(in_srgb,var(--flo-primary-dark-color)_24%,transparent)]'
                    }`}
                    style={isSelected ? { background: SELECTED_FILL } : undefined}
                  >
                    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                      <PlanTitle plan={plan} className={PREVIEW_TEXT.planTitle} />
                      {deliveries !== null && (
                        <span className={`${PREVIEW_TEXT.planMeta} text-black/50`}>
                          {t('subscriptions.preview_delivery_label', { count: deliveries })}
                        </span>
                      )}
                    </div>

                    <div className='flex shrink-0 flex-col items-end gap-0.5'>
                      <span className={`${PREVIEW_TEXT.planPrice} text-black/80`}>{PRICE_PLACEHOLDER}</span>
                      {Boolean(discount) && (
                        <span className={`${PREVIEW_TEXT.planMeta} text-black/50 line-through`}>
                          {STRIKE_PLACEHOLDER}
                        </span>
                      )}
                    </div>
                  </button>
                  {Boolean(discount) && (
                    <span
                      className={`relative z-[1] -mt-2 mr-3 flex h-[14px] items-center self-end rounded-full px-1.5 ${PREVIEW_TEXT.discountPill} text-white [text-shadow:0_0.5px_0_rgba(0,0,0,0.14)] ${DISCOUNT_PILL_SHADOW}`}
                      style={{ background: discountPill }}
                    >
                      {discount}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>

          <div className='h-9' />
        </div>
      </div>
    </div>
  )
}

export default ChoosePlanSheet
